import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button } from "../../components/button";
import { Chat } from "../../components/chat";
import { DropOverlay } from "../../components/drop-overlay";
import { Icon } from "../../components/icon";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { brigtenColor, darkenColor } from "../../ui/color";
import { runOperation } from "../../ui/operation";
import { cond, randomId } from "../../util";

export default function (ctx) {

    const chatClientKey = ctx.params.key;
    const chatClientData = el.state.create(null);

    var isRetrying;
    var playingAudio;
    var abortController;
    var chat;
    var colorStyles = el("style");
    var customStyles = el("style");

    async function reloadMessages() {

        await runOperation(null, async () => {
            const response = await api.getJson(`/api/v1/public/chat-clients/${chatClientKey}`);
            chatClientData.value = response.data;

            const client = chatClientData.value.client;

            if (client.customStyles) {
                customStyles.innerHTML = client.customStyles;
            }
            if (client.primaryColor) {
                const lightColors = brigtenColor(client.primaryColor, 3);
                const darkColors = darkenColor(client.primaryColor, 3);

                colorStyles.innerHTML = `
                    @media (prefers-color-scheme: light) {
                        :root {
                            --primary-color: ${lightColors[0]};
                            --primary-color-100: ${lightColors[1]};
                            --primary-color-200: ${lightColors[2]};
                            --primary-color-opaque: ${lightColors[0]}33;
                        }
                    }
                    
                    @media (prefers-color-scheme: dark) {
                        :root {
                            --primary-color: ${darkColors[0]};
                            --primary-color-100: ${darkColors[1]};
                            --primary-color-200: ${darkColors[2]};
                            --primary-color-opaque: ${lightColors[0]}33;
                        }
                    }
                `;
            }
            if (client.customScripts) {
                new Function(client.customScripts)();
            }
            if (client.pageTitle) {
                document.title = client.pageTitle;
            }
        });
    }

    async function handleDecodeLink(link) {
        return await api.fetch(`/api/v1/public/chat-clients/${chatClientKey}/link-spy?target=` + encodeURIComponent(link));
    }

    async function handleClear() {
        runOperation(null, async () => {
            const authChatId = chatClientData.value.chatCreationData?.chatClientId;
            if (authChatId) {
                const response = await api.postJson(`/api/v1/web-chat-client/${authChatId}/sessions`, {
                    tag: "manual-session@" + randomId(),
                    expires: 2592000 // 1 month
                });
                page(`/chat/${response.data.accessKey}`);

            } else {
                await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}/reset-state`);
                window.location.reload();
            }
        });
    }

    async function handleSinthetize({ sender, state, text }) {
        if (state == "play") {
            sender.setPlayingState("loading");
            const audioSrc = window.env.apiEndpoint + `/api/v1/public/chat-clients/${chatClientKey}/sinthetize?t=${encodeURIComponent(text)}`;
            playingAudio = new Audio(audioSrc);
            playingAudio.play();
            playingAudio.onended = function () {
                sender.setPlayingState(null);
            }
            playingAudio.onpaused = function () {
                sender.setPlayingState(null);
            }
            playingAudio.onplay = function () {
                sender.setPlayingState("playing");
            }
        } else if (state == "stop") {
            sender.setPlayingState(null);
            playingAudio?.pause();
        }
    }

    async function startInference(assistantMessage) {
        var isFinished = false;
        var dataReceived = false;
        const finished = () => {
            if (isFinished) {
                return;
            }

            assistantMessage.setAfter(null);
            assistantMessage.setDone();
            chat.isReadOnly = false;
            chat.renderCodeBlocks();
            chat.addLightboxes();
            isFinished = true;
            isRetrying = false;
        };

        const sse = api.createEventSource(`/api/v1/public/chat-clients/${chatClientKey}/sse`);
        sse.onerror = e => {
            if (dataReceived) {
                sse.close();
                finished();
            } else {
                setTimeout(() => {
                    startInference(assistantMessage);
                }, 1000);
            }
        }
        sse.onmessage = e => {
            if (e.data && e.data.startsWith("{")) {
                const payload = JSON.parse(e.data);
                const data = payload.data;

                if (payload.type == "chat.chunk" && data.chunk) {
                    dataReceived = true;

                    if (data.insertMode == "append") {

                        if (data.type == "reasoning") {
                            assistantMessage.appendReasoning(data.chunk);
                        } else {
                            assistantMessage.append(data.chunk);
                        }

                    } else if (data.insertMode == "replace") {
                        assistantMessage.setContent(data.chunk);
                    }
                    chat.scrollToBottom();

                } else if (payload.type == "chat.debug-info") {
                    console.log(`%c${payload.data.information}`, payload.data.style);

                } else if (payload.type == "chat.error") {
                    displayAlert({
                        message: chatClientData.value.culture.inferenceError,
                        buttons: [
                            {
                                label: "OK",
                                onClick(event) {
                                    event.target.closest("dialog").dismiss();
                                }
                            }
                        ]
                    });
                    assistantMessage.setError(chatClientData.value.culture.inferenceError);

                } else if (payload.type == "chat.inlineerror") {
                    dataReceived = true;
                    assistantMessage.setError(payload.data.error);

                } else if (payload.type == "chat.start-thinking") {
                    dataReceived = true;
                    assistantMessage.startImplicitThinking();
                }
            } else if (e.data == "[DONE]") {
                sse.close();
                finished();
            }
        }
    }

    async function retryContext(message) {
        message && message.remove && message.remove();

        isRetrying = true;
        await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}/retry`);
        await reloadMessages();

        chat.scrollToBottom();
        const assistantMessage = chat.createMessage("assistant");
        assistantMessage.setAfter(el(".chat-loading-spinner"));

        await startInference(assistantMessage);
    }

    async function handleMessage(message, attachments) {
        abortController = new AbortController();
        chat.isReadOnly = true;

        if (attachments?.length > 0) {
            console.log(message, attachments)
            chat.createMessage("user", [
                {
                    type: "text",
                    text: message
                },
                ...attachments.map(attach =>
                    cond(
                        [attach.type == "image", { type: "image_url", image_url: { url: attach.contentBase64 } }],
                        [attach.type == "audio", { type: "input_audio", input_audio: { data: attach.contentBase64 } }],
                        [attach.type == "document", { type: "file", file: { filedata: "!#[conversation-attachment filename=\"" + attach.name + "\"]" } }]
                    )
                )
            ]);
        } else {
            chat.createMessage("user", message);
        }

        const assistantMessage = chat.createMessage("assistant");
        assistantMessage.setAfter(el(".chat-loading-spinner"));
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

        await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}`, {
            messageData: message,
            attachments: attachments.map(attach => {
                return {
                    contentBase64: attach.contentBase64,
                    type: attach.type,
                    name: attach.name
                }
            }),
            userLocalTime: new Date().toLocaleString('sv').replace(' ', 'T'),
            userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }, {
            signal: abortController.signal
        });

        await startInference(assistantMessage);
    }

    reloadMessages();

    return el.state.listening([chatClientData], () =>
        el("main.chat-page",
            DropOverlay(ev => {
                [...ev.dataTransfer.files].forEach(file => {
                    chat.addFile(file);
                });
            }),
            chatClientData.value && el.fragment(
                Chat({
                    messageList: chatClientData.value.messages,
                    onDecodeLink: handleDecodeLink,
                    onMessage: handleMessage,
                    onRetry: retryContext,
                    onClear: handleClear,
                    onReturn: null,
                    onSinthetize: chatClientData.value.client.audioSynthesisEnabled && handleSinthetize,
                    clientOptions: chatClientData.value.client,
                    culture: chatClientData.value.culture,
                    disabledItemRow: [
                        Button(Icon("close-line"), chatClientData.value.culture.cancelButtonLabel, {
                            onClick() {
                                abortController.abort();
                                api.deleteJson(`/api/v1/public/chat-clients/${chatClientKey}`);
                            }
                        })
                    ],
                    extraItemsRow: [
                        !isRetrying && chatClientData.value.messages?.at(-1)?.role === "user" && Button(Icon("reset-right-line"), chatClientData.value.culture.tryAgain, {
                            onClick(event) {
                                retryContext();
                                event.target.closest(".app-button").remove();
                            }
                        })
                    ],
                    presentation: {
                        logoSrc: chatClientData.value.client.logoImageUrl,
                        helloTitle: chatClientData.value.client.helloLabel,
                        helloSubtitle: chatClientData.value.client.helloSubLabel
                    },
                    header: {
                        title: chatClientData.value.chatTitle || chatClientData.value.client.pageTitle
                    },
                    props: {
                        init() {
                            chat = this;
                        }
                    }
                }),
                customStyles,
                colorStyles
            )
        )
    )
}
